import {
  Button,
  Flex,
  PremiumBagIcon,
  PremiumBullseyeIcon,
  PremiumPhoneIcon,
  ProfileIcon,
  Typography,
} from '@iheartjane/reefer';
import { useState } from 'react';

import { BloomBankLinkingModalWithPrompt } from '@jane/bloom/components/BloomBankLinkingModal/BloomBankLinkingModalWithPrompt';
import { useFullDestination } from '@jane/bloom/hooks/navigation/useFullDestination';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { useAuthModal } from '@jane/bloom/providers/authModalProvider';
import {
  getSignInPromptDefaults,
  resolveSignInPromptColors,
} from '@jane/bloom/resources/signInPromptTheme';
import { trackHeaderTap } from '@jane/bloom/services/tracking/track';

import { Prompt } from './prompt/prompt';

import styles from './accountInfo.module.css';

type AccountInfoParams = {
  /** A header sign-in should chain into the post-sign-in bank-linking prompt. */
  canPromptBankLinking?: boolean;
  disabled?: boolean;
  /** Persists the bank-linking prompt dismissal (14-day cookie). */
  dismissBankLinkingPrompt?: () => void;
  dismissPrompt?: () => void;
  showPrompt?: boolean;
};

export const AccountInfo = ({
  canPromptBankLinking = false,
  disabled,
  dismissBankLinkingPrompt,
  dismissPrompt,
  showPrompt = false,
}: AccountInfoParams) => {
  const {
    featureFlags,
    pinnedStore,
    shoppingPreferences,
    theme,
    whoami: {
      user: { email: userEmail },
    },
  } = useRootLoaderData();
  const generatePath = useFullDestination();

  const { openAuthModal } = useAuthModal();
  const [showBankLinking, setShowBankLinking] = useState(false);

  // After sign-in, prompt eligible non-Gold-members to link a bank
  const openAuthModalWithPrompt = () =>
    openAuthModal({
      onSuccess: canPromptBankLinking
        ? () => setShowBankLinking(true)
        : undefined,
    });
  const lp = theme.bloom?.login_prompt;
  const defaults = getSignInPromptDefaults(
    pinnedStore.crm_integration === 'alpineiq'
  );

  // Use || (not ??) for string fallbacks so empty strings fall through to
  // defaults instead of rendering blank.
  const header = lp?.header || defaults.header;
  const line1 = lp?.desktop_line_1 || defaults.desktop_line_1;
  const line2 = lp?.desktop_line_2 || defaults.desktop_line_2;
  const line3 = lp?.desktop_line_3 || defaults.desktop_line_3;
  const ctaLabel = lp?.cta_label || defaults.cta_label;
  const { button, buttonText } = resolveSignInPromptColors(theme);

  const handleHeaderTap = (e: React.MouseEvent<Element>) => {
    if (disabled) {
      e.preventDefault();
    }
    trackHeaderTap(
      {
        reservationMode: shoppingPreferences?.reservationMode,
        tappable: 'profile icon',
      },
      featureFlags
    );
  };

  const loginButtonElement = (
    <Button.Icon
      aria-label={userEmail ? 'Account' : 'Sign in'}
      className={styles['profileIcon']}
      onClick={(e) => {
        handleHeaderTap(e);
        if (!userEmail) openAuthModalWithPrompt();
      }}
      icon={<ProfileIcon color="text-main" />}
      to={userEmail ? generatePath('/account') : undefined}
      type="button"
      variant="minimal"
    />
  );

  const bankLinkingPrompt = showBankLinking ? (
    <BloomBankLinkingModalWithPrompt
      onDismiss={() => dismissBankLinkingPrompt?.()}
      onRequestClose={() => setShowBankLinking(false)}
    />
  ) : null;

  if (showPrompt && dismissPrompt) {
    return (
      <>
        <Prompt
          dismissible
          dismissPrompt={dismissPrompt}
          showPrompt={showPrompt}
          target={loginButtonElement}
        >
          <Prompt.Content>
            <Flex
              flexDirection="column"
              gap={16}
              className={styles['promptIcons']}
            >
              <Typography variant="body-semibold">{header}</Typography>
              <Flex gap={16}>
                <PremiumPhoneIcon size="lg" color="inherit" />
                <Typography>{line1}</Typography>
              </Flex>
              <Flex gap={16}>
                <PremiumBullseyeIcon size="lg" color="inherit" />
                <Typography>{line2}</Typography>
              </Flex>
              <Flex gap={16}>
                <PremiumBagIcon size="lg" color="inherit" />
                <Typography>{line3}</Typography>
              </Flex>
              <Button
                variant="primary"
                full
                label={ctaLabel}
                onClick={() => openAuthModalWithPrompt()}
                style={{
                  backgroundColor: button,
                  borderColor: button,
                  color: buttonText,
                }}
              />
            </Flex>
          </Prompt.Content>
        </Prompt>
        {bankLinkingPrompt}
      </>
    );
  }

  return (
    <>
      {loginButtonElement}
      {bankLinkingPrompt}
    </>
  );
};
